import { getRcCode } from "@/data/rcCodeLookup";
import { getOverrideCode, getOverrideLabel } from "@/data/rcCodeOverrides";

// Per-category short codes for style options.
// Each prefix maps to a category-specific array; the code is `${prefix}${index+1}`.
// Used to display "Notch · LT1" alongside style names in dropdowns / cards.
export const STYLE_CODE_PREFIX = {
  // Jacket
  JACKET_BODY_STYLES: "BS",
  JACKET_FRONT_CLOSURES: "FC",
  LAPEL_TYPES: "LT",
  LAPEL_WIDTHS: "LW",
  WAIST_POCKET_TYPES: "WP",
  WAIST_POCKET_ANGLES: "PA",
  TICKET_POCKETS: "TP",
  CHEST_POCKETS: "CP",
  BUTTON_STYLES: "BT",
  EXTERIOR_STITCHES: "ES",
  SLEEVE_BUTTONHOLES: "SB",
  VENTS: "V",
  LAPEL_BUTTONHOLES: "LB",
  GORGE_LINES: "GL",
  CHEST_POCKET_POSTURES: "CPP",
  LOWER_POCKET_FLAP_WIDTHS: "LPF",
  TICKET_POCKET_POSTURES: "TPP",
  BACK_VENT_LENGTHS: "BV",
  ARMHOLE_ADJUSTMENTS: "AA",
  SHOULDER_POSTURE_ADJUSTMENTS: "SPA",
  // Pant
  PANT_BODY_STYLES: "PBS",
  PANT_FRONT_STYLES: "PFS",
  FRONT_POCKETS: "FP",
  BACK_POCKET_CLOSURES: "BPC",
  BOTTOM_FINISHINGS: "BF",
  BACK_POCKET_STYLES: "BPS",
  FRONT_CLOSURES_PANT: "FCP",
  WAISTBAND_STYLES: "WB",
  WAISTBAND_WIDTHS: "WW",
  // Vest
  VEST_LAPELS: "VL",
  VEST_FRONT_CLOSURES: "VFC",
  VEST_POCKET_TYPES: "VPT",
  VEST_POCKET_NUMBERS: "VPN",
  VEST_BACKS: "VB",
};

/** Returns the RC factory code for a given option.
 *  prefixOrField:
 *    • If it matches a RC_CODE_LOOKUP field name (e.g. "lapel_type"), look up the
 *      real factory code (e.g. "C-0001"). This is the preferred form.
 *    • Otherwise treat it as a legacy index-prefix ("LT", "BS") — but only return
 *      a code if no field-name match was possible. By default we now prefer
 *      RC codes and return "" when no mapping exists, so unmapped options are
 *      simply un-badged. */
export function getStyleCode(prefixOrField, options, value) {
  if (!value) return "";
  // 1. Live admin overrides (rc_codes collection) take precedence so renames /
  //    additions made in /admin/code-manager flow through without a rebuild.
  const ovr = getOverrideCode(prefixOrField, value);
  if (ovr) return ovr;
  // 2. Static RC lookup table.
  const rc = getRcCode(prefixOrField, value);
  if (rc) return rc;
  // 3. No mapping → no badge.
  return "";
}

/** Returns the human-facing label for a code badge — uses the admin's custom rename
 *  if one exists, otherwise the raw code (e.g. "C-0001"). */
export function getStyleCodeLabel(code) {
  if (!code) return "";
  return getOverrideLabel(code) || code;
}

/** Formats "Value · CODE" — used inside SelectItem and as the visible trigger label. */
export function formatStyleLabel(prefixOrField, options, value) {
  const code = getStyleCode(prefixOrField, options, value);
  if (!code) return value || "";
  return `${value} · ${code}`;
}
